import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import { newsArticles } from "@/mocks/news";
import Breadcrumb from "@/components/base/Breadcrumb";
import PageMeta from "@/components/base/PageMeta";

const ARTICLES_PER_PAGE = 5;

const siteUrl = import.meta.env.VITE_PUBLIC_SITE_URL || "https://hackerslodge.com";

const newsBlogSchema = {
  "@context": "https://schema.org",
  "@type": "Blog",
  url: `${siteUrl}/hacking-news`,
  name: "Hacking News — Hackers Lodge",
  description: "Stay updated with the latest cybersecurity news, hacker community updates, data breaches, and digital security trends from Hackers Lodge.",
  publisher: {
    "@type": "Organization",
    name: "Hackers Lodge",
    url: siteUrl,
  },
  blogPost: newsArticles.map((article) => ({
    "@type": "BlogPosting",
    headline: article.title,
    description: article.excerpt,
    url: `${siteUrl}/hacking-news/${article.slug}`,
    author: {
      "@type": "Person",
      name: article.author,
    },
    datePublished: article.date,
    image: article.image,
  })),
};

export default function HackingNewsPage() {
  const [activePage, setActivePage] = useState(1);

  const totalPages = Math.max(1, Math.ceil(newsArticles.length / ARTICLES_PER_PAGE));
  const startIndex = (activePage - 1) * ARTICLES_PER_PAGE;
  const currentArticles = newsArticles.slice(startIndex, startIndex + ARTICLES_PER_PAGE);

  const handlePageClick = (page: number) => {
    setActivePage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const getPageNumbers = (): (number | "...")[] => {
    const pages: (number | "...")[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (activePage > 3) pages.push("...");
      const start = Math.max(2, activePage - 1);
      const end = Math.min(totalPages - 1, activePage + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      if (activePage < totalPages - 2) pages.push("...");
      pages.push(totalPages);
    }
    return pages;
  };

  return (
    <div className="min-h-screen bg-[#0d1117]">
      <PageMeta
        title="Hacking News — Latest Cybersecurity News, Data Breaches &amp; Security Research"
        description="Stay updated with the latest cybersecurity news, hacker community updates, data breaches, and digital security trends from Hackers Lodge. Read expert analysis on hacking tools, cryptocurrency security, social media protection, and more."
        keywords="hacking news, cybersecurity news, data breach news, security research, ethical hacking news, cryptocurrency scam news, hacker news, digital security"
        canonicalUrl={`${siteUrl}/hacking-news`}
        ogType="website"
        schema={newsBlogSchema}
      />
      <Header />

      {/* Page Header Banner */}
      <div className="pt-20 md:pt-24 bg-[#333] text-white text-center py-6">
        <h1 className="text-lg font-medium tracking-wide">HACKING NEWS</h1>
      </div>

      <main className="py-8 md:py-12">
        <div className="w-full px-4 md:px-8 max-w-3xl mx-auto">
          <Breadcrumb items={[{ label: "Home", to: "/" }, { label: "Hacking News" }]} />
          <h2 className="sr-only">Latest Hacking News Articles</h2>
          {/* Articles List */}
          <div className="space-y-10">
            {currentArticles.map((article) => (
              <article key={article.id} className="border-b border-gray-700 pb-10">
                {/* Featured Image */}
                <Link to={`/hacking-news/${article.slug}`}>
                  <div className="h-64 md:h-80 overflow-hidden rounded mb-5">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                </Link>

                {/* Metadata */}
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500 mb-3 uppercase">
                  <span className="font-semibold">{article.category}</span>
                  <span><i className="far fa-comment mr-1" />{article.comments === 0 ? "No Comments" : `${article.comments} Comment${article.comments > 1 ? "s" : ""}`}</span>
                  <span>by {article.author}</span>
                  <span>{article.date}</span>
                </div>

                {/* Title */}
                <Link to={`/hacking-news/${article.slug}`}>
                  <h2 className="text-xl md:text-2xl font-semibold text-gray-200 hover:text-[#5bc0de] transition-colors leading-snug mb-3">
                    {article.title}
                  </h2>
                </Link>

                {/* Excerpt */}
                <p className="text-sm text-gray-400 leading-relaxed mb-4">
                  {article.excerpt} [...]
                </p>

                {/* Read More */}
                <Link
                  to={`/hacking-news/${article.slug}`}
                  className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-[#5bc0de] hover:text-[#46b8da] transition-colors"
                >
                  Read More
                  <i className="fas fa-chevron-right text-[10px]" />
                </Link>
              </article>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-1 mt-12">
              <button
                onClick={() => handlePageClick(Math.max(1, activePage - 1))}
                disabled={activePage === 1}
                className="w-8 h-8 flex items-center justify-center text-xs text-gray-400 hover:text-gray-400 disabled:opacity-30 transition-colors cursor-pointer"
              >
                <i className="fas fa-angle-left" />
              </button>
              {getPageNumbers().map((p, i) =>
                p === "..." ? (
                  <span key={`dots-${i}`} className="text-xs text-gray-400 px-1">...</span>
                ) : (
                  <button
                    key={p}
                    onClick={() => handlePageClick(p)}
                    className={`w-8 h-8 flex items-center justify-center text-xs font-semibold transition-colors cursor-pointer ${
                      activePage === p ? "text-gray-200 border-b-2 border-gray-800" : "text-gray-400 hover:text-gray-400"
                    }`}
                  >
                    {p}
                  </button>
                )
              )}
              <button
                onClick={() => handlePageClick(Math.min(totalPages, activePage + 1))}
                disabled={activePage === totalPages}
                className="w-8 h-8 flex items-center justify-center text-xs text-gray-400 hover:text-gray-400 disabled:opacity-30 transition-colors cursor-pointer"
              >
                <i className="fas fa-angle-right" />
              </button>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}